import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Save, X } from "lucide-react";

const ALL_PLATFORMS = [
  { key: "facebook", label: "Facebook", color: "bg-[#1877F2]" },
  { key: "instagram", label: "Instagram", color: "bg-[#E1306C]" },
  { key: "linkedin", label: "LinkedIn", color: "bg-[#0A66C2]" },
  { key: "linkedin_ads", label: "LinkedIn Ads", color: "bg-[#0A66C2]" },
  { key: "linkedin_pages", label: "LinkedIn Pages", color: "bg-[#0A66C2]" },
  { key: "youtube", label: "YouTube", color: "bg-[#FF0000]" },
  { key: "x_twitter", label: "X (Twitter)", color: "bg-zinc-900" },
  { key: "tiktok", label: "TikTok", color: "bg-zinc-900" },
  { key: "pinterest", label: "Pinterest", color: "bg-[#E60023]" },
  { key: "google_ads", label: "Google Ads", color: "bg-[#4285F4]" },
  { key: "google_analytics", label: "GA4", color: "bg-[#F9AB00]" },
  { key: "google_search_console", label: "Search Console", color: "bg-[#4285F4]" },
  { key: "awstats", label: "AWStats", color: "bg-zinc-600" },
  { key: "bing_ads", label: "Bing Ads", color: "bg-[#008373]" },
  { key: "bing_analytics", label: "Bing Webmaster", color: "bg-[#008373]" },
  { key: "microsoft_clarity", label: "MS Clarity", color: "bg-[#5C2D91]" },
  { key: "openai_ads", label: "OpenAI Ads", color: "bg-[#10A37F]" },
];

export const PLATFORM_META = ALL_PLATFORMS.reduce((acc, p) => {
  acc[p.key] = p;
  return acc;
}, {});

export function PlatformPills({ keys = [], onEdit }) {
  if (!keys.length) {
    return (
      <button
        type="button"
        onClick={onEdit}
        className="inline-flex items-center text-[10px] uppercase tracking-wider text-zinc-400 hover:text-[#002FA7] transition-colors"
        data-testid="bu-no-platforms"
      >
        + add platforms
      </button>
    );
  }
  return (
    <div className="flex flex-wrap gap-1 items-center">
      {keys.map((k) => {
        const m = PLATFORM_META[k];
        if (!m) return null;
        return (
          <span
            key={k}
            className={`inline-flex items-center text-white text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-sm ${m.color}`}
            data-testid={`bu-platform-pill-${k}`}
            title={m.label}
          >
            {m.label}
          </span>
        );
      })}
      {onEdit && (
        <button
          type="button"
          onClick={onEdit}
          className="text-[10px] uppercase tracking-wider text-zinc-400 hover:text-[#002FA7] transition-colors ml-1"
          data-testid="bu-edit-platforms-link"
        >
          edit
        </button>
      )}
    </div>
  );
}

export default function PropertyPlatformsEditor({ businessUnitId, initial, onClose, onSaved }) {
  const [active, setActive] = useState(initial || []);
  const [busy, setBusy] = useState(false);

  useEffect(() => { setActive(initial || []); }, [initial]);

  const toggle = (k) =>
    setActive((prev) => (prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]));

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/business-units/${businessUnitId}`, {
        active_platforms: active,
      });
      toast.success("Platforms updated");
      onSaved?.(active);
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <Card className="rounded-sm border-zinc-200 bg-zinc-50/60" data-testid="bu-platforms-editor">
      <CardContent className="p-5 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <div className="label-overline">Marketing channels</div>
            <h3 className="font-display font-bold text-base tracking-tight">
              Which platforms are used for this property?
            </h3>
            <p className="text-xs text-zinc-500 mt-1">
              Only the platforms checked here will show up when uploading data, and
              only they appear in the property&apos;s section of the PDF report.
            </p>
          </div>
          {onClose && (
            <Button variant="ghost" size="sm" className="rounded-sm" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 pt-1">
          {ALL_PLATFORMS.map((p) => {
            const on = active.includes(p.key);
            return (
              <label
                key={p.key}
                className={`flex items-center gap-2 px-3 py-2 rounded-sm border cursor-pointer text-sm
                  ${on ? "border-[#002FA7] bg-white shadow-sm" : "border-zinc-200 bg-white/60 hover:bg-white"}`}
                data-testid={`bu-platform-checkbox-${p.key}`}
              >
                <input
                  type="checkbox"
                  checked={on}
                  onChange={() => toggle(p.key)}
                  className="h-4 w-4 accent-[#002FA7]"
                />
                <span className={`inline-block h-2 w-2 rounded-sm ${p.color}`} />
                <span className="flex-1">{p.label}</span>
              </label>
            );
          })}
        </div>

        <div className="flex items-center gap-2 pt-3 border-t border-zinc-200">
          <Button
            onClick={save}
            disabled={busy}
            className="rounded-sm bg-[#002FA7] hover:bg-[#00227A] text-white"
            data-testid="bu-platforms-save"
          >
            <Save className="h-3.5 w-3.5 mr-1.5" />
            {busy ? "Saving…" : "Save"}
          </Button>
          {onClose && (
            <Button variant="ghost" className="rounded-sm" onClick={onClose}>Cancel</Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
